import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./App.css";
import { useAuth } from "./AuthContext";
import Footer from "./Footer";

function FadeInSection({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.15 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ease-out transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // --- pricing state ---
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const isAnnual = billingPeriod === "annual";

  // Adjust prices as you like
  const starterPrice = isAnnual ? 15 : 19;
  const studioPrice = isAnnual ? 39 : 49;




  return (
    <>
      <div className="w-full flex justify-center text-[#eee] bg-[#111] min-h-screen">

        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(to right, rgba(255,255,255,0.06) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(255,255,255,0.06) 1px, transparent 1px)
            `,
            backgroundSize: "32px 32px",
            opacity: 1,
            zIndex: 0,
            maskImage: "radial-gradient(circle at center, white, transparent 75%)",
            WebkitMaskImage: "radial-gradient(circle at center, white, transparent 75%)"
          }}
          aria-hidden="true"
        />

        <div className="w-full max-w-[1100px] px-6 md:px-4 pb-20 relative">
          {/* Header */}
          <div className="w-full flex flex-row items-center justify-between mt-6 mb-16 relative z-10">
            <div className="flex flex-row items-center gap-x-2">
              <h1 className="text-3xl font-bold font-bebas tracking-wider">
                PLAYSET
              </h1>
            </div>

            <button
              onClick={() => navigate(currentUser ? "/dashboard" : "/login")}
              className="px-4 py-2 bg-yellow-500/20 border border-yellow-500 text-white rounded-xl cursor-pointer hover:bg-yellow-500 transition"
            >
              {currentUser ? "Dashboard" : "Sign In"}
            </button>
          </div>

          {/* <div
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage: `
              linear-gradient(to right, rgba(255,255,255,0.06) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(255,255,255,0.06) 1px, transparent 1px)
            `,
              backgroundSize: "32px 32px",
              opacity: 1,
              zIndex: 0,
              maskImage:
                "radial-gradient(circle at center, white, transparent 75%)",
              WebkitMaskImage:
                "radial-gradient(circle at center, white, transparent 75%)",
            }}
            aria-hidden="true"
          /> */}

          {/* Hero */}
          {/* <section className="flex justify-center relative z-10">
            <div className="relative inline-block font-bebas text-[4rem] md:text-7xl text-[#eee] font-bold text-center tracking-wider leading-none">
              Playtesting, in <span className="text-yellow-500">one click</span>.
              <img
                src="/pointinghand.svg"
                alt="Pointing hand"
                className="pointer-events-none absolute h-10 md:h-24 object-contain -right-4 md:-right-4 -bottom-12 md:-bottom-12 -rotate-[20deg]"
              />
            </div>
          </section>

          <div className="text-xl text-[#eee] font-bold text-center tracking-wider leading-none mt-4 relative z-10">
            <span className="font-nunito">
              Upload your{" "}
              <code className="border border-[#eee]/60 p-1 px-2 rounded-xl bg-[#eee]/10 text-lg">
                .zip
              </code>{" "}
              to get a shareable link with all this{" "}
            </span>
            <span className="">⤵</span>
          </div> */}

          <section className="flex justify-center relative z-10">
            <div className="relative inline-block font-bebas text-[4rem] md:text-7xl text-[#eee] font-bold text-center tracking-wider leading-none">
              See your game through their eyes
              {/* <img
                src="/pointinghand.svg"
                alt="Pointing hand"
                className="pointer-events-none absolute h-10 md:h-24 object-contain -right-4 md:-right-4 -bottom-12 md:-bottom-12 -rotate-[20deg]"
              /> */}
            </div>
          </section>

          <div className="flex flex-col md:flex-row justify-center text-xl text-[#eee] font-bold text-center tracking-wider leading-snug mt-4 relative z-10 mx-auto space-x-4">
            <span className="font-nunito">
              Frictionless playtesting.
            </span>
            <span className="font-nunito">
              Auto-recorded video.
            </span>
            <span className="font-nunito">
              Timestamped feedback.
            </span>
          </div>

          <section className="flex flex-row justify-center mt-8 gap-x-6 relative z-10">
            <button
              onClick={() => navigate(currentUser ? "/dashboard" : "/login")}
              className="px-4 py-2 bg-yellow-500/20 border border-yellow-500 text-white rounded-xl cursor-pointer hover:bg-yellow-500 transition"
            >
              {currentUser ? "Go to Dashboard" : "Start for free"}
            </button>
          </section>

          <FadeInSection className="w-full relative pb-[56.25%] mb-12 mt-8 z-10">
            <img
              src={
                "/demoScreenshot.png"
              }
              className="absolute top-0 left-0 w-full h-full rounded-2xl shadow-md border border-[#eee]/30 border-dashed"
              alt="Playset screenshot"
            />
          </FadeInSection>

          {/* Features */}
          <FadeInSection className="mt-20 relative z-10">
            <section>
              <h2 className="font-bebas text-5xl tracking-wider mb-2">
                FEATURES
              </h2>
              <p className="font-nunito text-base text-[#fff]/80">
                Everything you need to run real playtests in minutes — without
                stitching together a dozen tools.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                <div className="bg-[#161616] border border-[#2a2a2a] rounded-2xl p-5">
                  <p className="text-2xl mb-2">⚡</p>
                  <h3 className="font-nunito font-semibold text-lg mb-1 capitalize">
                    One-click uploads
                  </h3>
                  <p className="text-base text-[#aaa]">
                    Drop in a web build{" "}
                    <span className="text-xs">(.zip)</span>, get an instant
                    hosted link you can send anywhere — Discord, email, X, you
                    name it.
                  </p>
                </div>

                <div className="bg-[#161616] border border-[#2a2a2a] rounded-2xl p-5">
                  <p className="text-2xl mb-2">📸</p>
                  <h3 className="font-nunito font-semibold text-lg mb-1 capitalize">
                    Complete Video & Screenshots
                  </h3>
                  <p className="text-base text-[#aaa]">
                    Every playtest includes a full video recording of the session, and players can capture screenshots
                    directly from the browser.
                  </p>
                </div>

                <div className="bg-[#161616] border border-[#2a2a2a] rounded-2xl p-5">
                  <p className="text-2xl mb-2">🎯</p>
                  <h3 className="font-nunito font-semibold text-lg mb-1 capitalize">
                    Timestamped feedback
                  </h3>
                  <p className="text-base text-[#aaa]">
                    Testers log bugs, ideas, and react to moments while they
                    play with frictionless controls — all pinned to the exact
                    moment in the session.
                  </p>
                </div>

                <div className="bg-[#161616] border border-[#2a2a2a] rounded-2xl p-5">
                  <p className="text-2xl mb-2">💬</p>
                  <h3 className="font-nunito font-semibold text-lg mb-1 capitalize">
                    Live & async
                  </h3>
                  <p className="text-base text-[#aaa]">
                    See playtest events as they come in and chat with players in real time, or
                    review the full session timeline with video later.
                  </p>
                </div>

                <div className="bg-[#161616] border border-[#2a2a2a] rounded-2xl p-5">
                  <p className="text-2xl mb-2">📊</p>
                  <h3 className="font-nunito font-semibold text-lg mb-1 capitalize">
                    Metered by Actual Minutes Played
                  </h3>
                  <p className="text-base text-[#aaa]">
                    Spread your monthly minutes across as many games and testers as you like — only in-game time counts.
                  </p>
                </div>

                <div className="bg-[#161616] border border-[#2a2a2a] rounded-2xl p-5">
                  <p className="text-2xl mb-2">🔐</p>
                  <h3 className="font-nunito font-semibold text-lg mb-1 capitalize">
                    No SDK, no install
                  </h3>
                  <p className="text-base text-[#aaa]">
                    Everything runs in the browser. No tracking SDKs, no extra
                    build targets — just upload, share, and test.
                  </p>
                </div>
              </div>
            </section>
          </FadeInSection>

          {/* Comparison */}
          <FadeInSection className="mt-20 relative z-10" delay={100}>
            <section>
              <h2 className="font-bebas text-5xl tracking-wider mb-2">
                PLAYSET VS "THE DIY STACK"
              </h2>
              <p className="font-nunito text-base text-[#fff]/80">
                Stop juggling Discord, Google Forms, random screen recordings,
                and spreadsheets just to understand what&apos;s going on in your
                game.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div className="bg-[#161616] border border-yellow-500 rounded-2xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bebas font-semibold text-2xl tracking-wider">
                      Playset
                    </h3>
                  </div>
                  <ul className="text-base text-[#ddd] space-y-2 list-disc list-inside">
                    <li>One link per game, with builds and sessions in one place.</li>
                    <li>Automatic video recording and feedback buttons for testers.</li>
                    <li>Every action auto-timestamped within the session.</li>
                    <li>All your playtests stored and searchable in one hub.</li>
                    {/* <li>AI spots patterns: repeat bugs and pain points.</li> */}
                    <li>No SDKs, no installs.</li>
                  </ul>
                </div>

                <div className="bg-[#161616] border border-[#2a2a2a] rounded-2xl p-5 opacity-90">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-nunito font-semibold text-lg">
                      "The DIY Stack"
                    </h3>
                    <span className="text-xs font-nunito bg-[#333]/80 border border-[#555] px-2 py-0.5 rounded-xl">
                      Screen recorder + Discord + forms
                    </span>
                  </div>
                  <ul className="text-base text-[#aaa] space-y-2 list-disc list-inside">
                    <li>New build? Resend links to every tester.</li>
                    <li>Rely on testers to record video, take screenshots and notes.</li>
                    <li>No clear timeline of where they got stuck or bored.</li>
                    {/* <li>Feedback split across chats, forms, and files.</li> */}
                    <li>Spot patterns the hard way — manually.</li>
                  </ul>
                </div>
              </div>
            </section>
          </FadeInSection>

          {/* Engine support */}
          <FadeInSection className="mt-20 relative z-10" delay={150}>
            <section>
              <h2 className="font-bebas text-5xl tracking-wider mb-2">
                WORKS WITH YOUR ENGINE
              </h2>
              <p className="font-nunito text-base text-[#fff]/80">
                If your game can run in a browser, you can playtest it with
                Playset.
              </p>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Godot */}
                <div className="bg-[#161616] border border-[#2a2a2a] rounded-2xl p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <img
                      src="/godot_logo.svg"
                      alt="Godot logo"
                      className="h-12 object-contain"
                    />
                  </div>
                  <p className="text-base text-[#aaa]">
                    Export your Godot to HTML5, zip your build, upload —
                    Playset handles the rest.
                  </p>
                </div>

                {/* Unity */}
                <div className="bg-[#161616] border border-[#2a2a2a] rounded-2xl p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <img
                      src="/unity_logo.svg"
                      alt="Unity logo"
                      className="h-12 object-contain invert"
                    />
                  </div>
                  <p className="text-base text-[#aaa]">
                    Unity WebGL builds work out of the box, so your testers just
                    click and play.
                  </p>
                </div>

                {/* Other web builds */}
                <div className="bg-[#161616] border border-[#2a2a2a] rounded-2xl p-5">
                  {/* <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-nunito font-semibold text-base">
                      Other web builds
                    </h3>
                  </div> */}
                  <div className="flex items-center gap-3 mb-2">
                    <img
                      src="/js_logo.png"
                      alt="Unity logo"
                      className="h-12 object-contain rounded-lg"
                    />
                  </div>
                  <p className="text-base text-[#aaa]">
                    Custom engines, frameworks, or raw JS — as long as it&apos;s
                    a browser game packaged as a .zip, you&apos;re good.
                  </p>
                </div>
              </div>

              <p className="font-nunito text-sm text-[#fff]/80 mt-3">
                Native builds coming later — starting with web keeps things{" "}
                <span className="italic">frictionless</span> for you and your
                testers.
              </p>
            </section>
          </FadeInSection>


          {/* PRICING */}
          <FadeInSection className="mt-20 relative z-10" delay={200}>
            <section>
              <h2 className="font-bebas text-5xl tracking-wider mb-2">
                PRICING
              </h2>
              <p className="font-nunito text-base text-[#fff]/80">
                Built for indie budgets. No four-figure enterprise contracts
                just to watch someone play your game.
              </p>

              <div className="overflow-visible mt-6 -mx-6">
                <table className="w-full table-fixed border-separate border-spacing-x-6 border-spacing-y-0">
                  <colgroup>
                    <col className="w-1/4" />
                    <col className="w-1/4" />
                    <col className="w-1/4" />
                    <col className="w-1/4" />
                  </colgroup>

                  <thead>
                    {/* HEADER ROW: name + copy */}
                    <tr>
                      {/* Free */}
                      <td className="align-top bg-[#161616] border border-b-0 border-[#2a2a2a] rounded-t-2xl p-5 pb-2">
                        <p className="text-3xl font-bebas tracking-wider mb-1">FREE</p>
                        <p className="text-sm text-[#aaa]">
                          Discover what Playset can do with your first game.
                        </p>
                      </td>

                      {/* Starter */}
                      <td className="align-top bg-[#161616] border border-b-0 border-[#2a2a2a] rounded-t-2xl p-5 pb-2">
                        <p className="text-3xl font-bebas tracking-wider mb-1">
                          STARTER PACK
                        </p>
                        <p className="text-sm text-[#aaa]">
                          Ideal for early prototypes, jam games, or a small beta group.
                        </p>
                      </td>

                      {/* Studio */}
                      <td className="relative align-top bg-[#161616] border border-b-0 border-yellow-500 rounded-t-2xl p-5 pb-2">
                        <div
                          className="absolute -top-3 left-1/2 -translate-x-1/2 
                  bg-yellow-500 text-[0.65rem] md:text-xs uppercase font-semibold 
                  px-6 py-1 rounded-full text-[#111] shadow 
                  inline-flex items-center justify-center whitespace-nowrap"
                        >
                          Recommended choice
                        </div>

                        <p className="text-3xl font-bebas tracking-wider mb-1">
                          STUDIO PACK
                        </p>
                        <p className="text-sm text-[#aaa]">
                          Perfect for ongoing betas, Steam demos, or frequent update cycles.
                        </p>
                      </td>

                      {/* Custom */}
                      <td className="align-top bg-[#161616] border border-b-0 border-[#2a2a2a] rounded-t-2xl p-5 pb-2">
                        <p className="text-3xl font-bebas tracking-wider mb-1">
                          CUSTOM PLAN
                        </p>
                        <p className="text-sm text-[#aaa]">
                          Bigger team? Multiple projects? We’ll design a plan that fits.
                        </p>
                      </td>
                    </tr>
                  </thead>

                  <tbody>
                    {/* PRICE ROW */}
                    <tr>
                      {/* Free */}
                      <td className="align-top bg-[#161616] border-x border-[#2a2a2a] p-5 pt-0 pb-4">
                        <div className="flex items-baseline gap-2 mt-2">
                          <p className="text-3xl font-bebas tracking-wider">$0</p>
                          <span className="text-xs font-nunito text-[#aaa]">
                            per month
                          </span>
                        </div>
                      </td>

                      {/* Starter */}
                      <td className="align-top bg-[#161616] border-x border-[#2a2a2a] p-5 pt-0 pb-4">
                        <div className="flex items-baseline gap-2 mt-2">
                          <p className="text-3xl font-bebas tracking-wider">
                            ${starterPrice}
                          </p>
                          <span className="text-xs font-nunito text-[#aaa]">
                            per month{isAnnual && ", billed annually"}
                          </span>
                        </div>
                      </td>

                      {/* Studio */}
                      <td className="align-top bg-[#161616] border-x border-yellow-500 p-5 pt-0 pb-4">
                        <div className="flex items-baseline gap-2 mt-2">
                          <p className="text-3xl font-bebas tracking-wider">
                            ${studioPrice}
                          </p>
                          <span className="text-xs font-nunito text-[#aaa]">
                            per month{isAnnual && ", billed annually"}
                          </span>
                        </div>
                      </td>

                      {/* Custom (no price) */}
                      <td className="align-top bg-[#161616] border-x border-[#2a2a2a] p-5 pt-0 pb-4" />
                    </tr>

                    {/* TOGGLE ROW */}
                    <tr>
                      {/* Free – empty cell */}
                      <td className="bg-[#161616] border border-[#2a2a2a] border-t-0 p-5 pt-0" />

                      {/* Starter toggle */}
                      <td className="bg-[#161616] border border-[#2a2a2a] border-t-0 p-5 pt-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() =>
                                setBillingPeriod(isAnnual ? "monthly" : "annual")
                              }
                              className="relative w-12 h-6 rounded-full bg-[#262626] flex items-center px-1"
                              aria-label="Toggle Starter billing period"
                            >
                              <span
                                className={`inline-block h-4 w-4 rounded-full bg-white transform transition-transform duration-200 ${isAnnual ? "translate-x-6" : ""
                                  }`}
                              />
                            </button>
                            <span
                              className={`text-xs font-nunito ${isAnnual ? "text-white" : "text-[#777]"
                                }`}
                            >
                              Annual
                            </span>
                          </div>
                          <span
                            className={`text-xs font-nunito text-green-400 transition ${isAnnual ? "opacity-100" : "opacity-0"
                              }`}
                          >
                            Save $48
                          </span>
                        </div>
                      </td>

                      {/* Studio toggle */}
                      <td className="bg-[#161616] border border-yellow-500 border-b-[#2a2a2a] border-t-0 p-5 pt-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() =>
                                setBillingPeriod(isAnnual ? "monthly" : "annual")
                              }
                              className="relative w-12 h-6 rounded-full bg-[#262626] flex items-center px-1"
                              aria-label="Toggle Studio billing period"
                            >
                              <span
                                className={`inline-block h-4 w-4 rounded-full bg-white transform transition-transform duration-200 ${isAnnual ? "translate-x-6" : ""
                                  }`}
                              />
                            </button>
                            <span
                              className={`text-xs font-nunito ${isAnnual ? "text-white" : "text-[#777]"
                                }`}
                            >
                              Annual
                            </span>
                          </div>
                          <span
                            className={`text-xs font-nunito text-green-400 transition ${isAnnual ? "opacity-100" : "opacity-0"
                              }`}
                          >
                            Save $120
                          </span>
                        </div>
                      </td>

                      {/* Custom – empty */}
                      <td className="bg-[#161616] border border-[#2a2a2a] border-t-0 p-5 pt-0" />
                    </tr>

                    {/* CTA ROW */}
                    <tr>
                      <td className="bg-[#161616] border border-[#2a2a2a] border-t-0 p-5">
                        <button 
                          onClick={() => navigate(currentUser ? "/dashboard" : "/login")}
                          className="inline-flex w-full items-center justify-center rounded-xl bg-[#262626] px-4 py-2 text-base font-nunito font-semibold hover:bg-[#333] transition"
                        >
                          Get started
                        </button>
                      </td>

                      <td className="bg-[#161616] border border-[#2a2a2a] border-t-0 p-5">
                        <button 
                          onClick={() => navigate("/dashboard/purchase")}
                          className="inline-flex w-full items-center justify-center rounded-xl bg-yellow-500 text-black px-4 py-2 text-base font-nunito font-semibold hover:bg-yellow-600 transition"
                        >
                          Choose Starter
                        </button>
                      </td>

                      <td className="bg-[#161616] border border-yellow-500 border-b-[#2a2a2a] border-t-0 p-5">
                        <button 
                          onClick={() => navigate("/dashboard/purchase")}
                          className="inline-flex w-full items-center justify-center rounded-xl bg-yellow-500 text-black px-4 py-2 text-base font-nunito font-semibold hover:bg-yellow-600 transition"
                        >
                          Choose Studio
                        </button>
                      </td>

                      <td className="bg-[#161616] border border-[#2a2a2a] border-t-0 p-5">
                        <button 
                          onClick={() => navigate("/dashboard/support")}
                          className="inline-flex w-full items-center justify-center rounded-xl bg-[#7c3aed] px-4 py-2 text-base font-nunito font-semibold hover:bg-[#8d5aef] transition"
                        >
                          Talk to us
                        </button>
                      </td>
                    </tr>

                    {/* FEATURES ROW */}
                    <tr>
                      <td className="align-top bg-[#161616] border border-[#2a2a2a] border-t-0 rounded-b-2xl p-5">
                        <ul className="space-y-2 text-sm text-[#aaa] pricing-bullets">
                          <li>15 mins of playtesting each month</li>
                          <li>1 game, up to 1GB</li>
                          <li>Any number of testers & sessions</li>
                          <li>Full recordings & feedback timeline</li>
                        </ul>
                      </td>

                      <td className="align-top bg-[#161616] border border-[#2a2a2a] border-t-0 rounded-b-2xl p-5">
                        <ul className="space-y-2 text-sm text-[#aaa] pricing-bullets">
                          <li>10 hours of playtesting each month</li>
                          <li>1 game, up to 2GB</li>
                          <li>Metered by actual minutes played</li>
                          <li>Any number of testers & sessions</li>
                          <li>Full recordings & feedback timeline</li>
                        </ul>
                      </td>

                      <td className="align-top bg-[#161616] border border-yellow-500 border-t-0 rounded-b-2xl p-5">
                        <ul className="space-y-2 text-sm text-[#aaa] pricing-bullets">
                          <li>50 hours of playtesting each month</li>
                          <li>Up to 3 games, 3GB each</li>
                          <li>Metered by actual minutes played</li>
                          <li>Any number of testers & sessions</li>
                          <li>Full recordings & feedback timeline</li>
                        </ul>
                      </td>

                      <td className="align-top bg-[#161616] border border-[#2a2a2a] border-t-0 rounded-b-2xl p-5">
                        <ul className="space-y-2 text-sm text-[#aaa] pricing-bullets">
                          <li>More games & storage</li>
                          <li>Volume discounts</li>
                          <li>Longer recording retention</li>
                          <li>Ideal for studios, publishers & labs</li>
                        </ul>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
          </FadeInSection>


          <FadeInSection className="flex flex-row justify-center mt-20 gap-x-6 relative z-10" delay={250}>
            <section className="flex flex-row justify-center gap-x-6">
              <button
                onClick={() => navigate(currentUser ? "/dashboard" : "/login")}
                className="px-4 py-2 bg-yellow-500/20 border border-yellow-500 text-white rounded-xl cursor-pointer hover:bg-yellow-500 transition"
              >
                {currentUser ? "Go to Dashboard" : "Start for free"}
              </button>
            </section>
          </FadeInSection>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </>
  );
}
